import { DateTimeUnitConfigsMap, Filter, isAdvancedFilter, isBasicFilter, isDynamicFilter, isExprFilter, isRelativeDateFilter, isRelativeTimeFilter, isTopNFilter } from 'src/app/shared/compatibility/filter.models';
import { VisualFieldTarget } from '../visuals/VisualFieldTarget';
import { AdvancedFilterConditionDto, AdvancedFilterConditionOperator, BasicFilterOperator, DynamicFilterValue, FilterDto, RelativeDateOrTimeOperator, RelativeDateTimeFilterUnit, SchemaFieldTargetDto, SchemaTargetDto, TopNFilterOperator } from 'src/app/services/api.services';
import { VisualFieldTargetResolver } from '../visuals/VisualFieldTargetResolver';
import { DataFormatter } from '../formatting/DataFormatter';
import { escapeHtml, isNullOrUndefined } from 'src/app/shared/utils';

export namespace FilterUtils {
  export function getTarget(filter: FilterDto): SchemaFieldTargetDto {
    return isTopNFilter(filter) ? filter.orderBy : (filter as any).target;
  }

  export function getSchemaTarget(filter: FilterDto): SchemaTargetDto {
    return getTarget(filter).schema;
  }

  function capitalizeFirstWord(str: string) {
    return str.charAt(0).toUpperCase() + str.substring(1);
  }

  export class Formatter {
    constructor(
      readonly fieldResolver: VisualFieldTargetResolver,
      readonly dataFormatter: DataFormatter) { }

    formatDescription(filter: FilterDto, options?: { 
      capitalizeFirstWord?: boolean; 
      noHtml?: boolean;
      longFieldTitles?: boolean;
      noTarget?: boolean;
      targetFormatter?: ((t: VisualFieldTarget) => string),
      targetWrapper?: ((str: string, t?: VisualFieldTarget) => string),
    }) {
      let longFieldTitles = !!options?.longFieldTitles;
      let noTarget = !!options?.noTarget;

      let text = [];
      
      let target: VisualFieldTarget;
      let prefixWithTargetText = false;

      let targetWrapper = options?.targetWrapper ?? (
        options?.noHtml 
          ? ((str) => str)
          : ((str, t) => `<strong class="${(t.isValid ? "" : "c-red-2")}">${escapeHtml(str)}</strong>`)
      );

      let targetFormatter = options?.targetFormatter ?? (
        (t) => target.aggregation 
             ? target.aggregation.formatAggregationOfText(longFieldTitles ? target.longTitle : target.shortTitle) 
             : longFieldTitles ? target.longTitle : target.shortTitle
      );

      let targetText: string;
      let getTargetText = () => targetText ??= targetWrapper(targetFormatter(target), target);

      if (isAdvancedFilter(filter)) {
        target = this.fieldResolver.find(filter.target);
        prefixWithTargetText = !noTarget;

        if (filter.conditions.length > 0) {
          text.push(this.formatCondition(target, filter.conditions[0]));

          if (filter.conditions.length > 1) {
            text.push(' ', filter.logicalOperator.toLowerCase(), ' ');
            text.push(this.formatCondition(target, filter.conditions[1]));
          }
        }
      }
      else if (isRelativeDateFilter(filter) || isRelativeTimeFilter(filter)) {
        target = this.fieldResolver.find(filter.target);
        prefixWithTargetText = !noTarget;

        if (filter.operator === RelativeDateOrTimeOperator.InThis) {
          switch (filter.timeUnitType) {
            case RelativeDateTimeFilterUnit.Days: text.push('is in this day'); break;

            case RelativeDateTimeFilterUnit.Weeks:         text.push('is in this week'); break;
            case RelativeDateTimeFilterUnit.CalendarWeeks: text.push('is in this calendar week'); break;

            case RelativeDateTimeFilterUnit.Months:         text.push('is in this month'); break;
            case RelativeDateTimeFilterUnit.CalendarMonths: text.push('is in this calendar month'); break;

            case RelativeDateTimeFilterUnit.Years:         text.push('is in this year'); break;
            case RelativeDateTimeFilterUnit.CalendarYears: text.push('is in this calendar year'); break;

            case RelativeDateTimeFilterUnit.CalendarHalfYears: text.push('is in this calendar half year'); break;

            case RelativeDateTimeFilterUnit.Quarters:         text.push('is in this quarter'); break;
            case RelativeDateTimeFilterUnit.CalendarQuarters: text.push('is in this calendar quarter'); break;

            case RelativeDateTimeFilterUnit.SpendPeriod: text.push('is in this period'); break;

            case RelativeDateTimeFilterUnit.Minutes: text.push('is in this minute'); break;
            case RelativeDateTimeFilterUnit.Hours:   text.push('is in this hour'); break;
          }
        }
        else if (filter.operator == RelativeDateOrTimeOperator.InRange 
              || filter.operator == RelativeDateOrTimeOperator.OutsideRange) {
          let isInRange = filter.operator === RelativeDateOrTimeOperator.InRange;

          let pastText   = isInRange ? 'is in the past ' : 'is before the last ';
          let lastText   = isInRange ? 'is in the last ' : 'is before the last ';

          let futureText = isInRange ? ' and in the future ' : ' and after the next ';
          let nextText   = isInRange ? ' and in the next '   : ' and after the next ';

          text.push(filter.timeUnitsCount === 0 ? pastText : lastText);
          if (filter.timeUnitsCount !== 0) {
            text.push(this.formatUnit(filter.timeUnitType, filter.timeUnitsCount));
          }

          text.push(filter.timeUnitsCount2 === 0 ? futureText : nextText);
          if (filter.timeUnitsCount2 !== 0) {
            text.push(this.formatUnit(filter.timeUnitType2, filter.timeUnitsCount2));
          }
        }
        else {
          switch (filter.operator) {
            case RelativeDateOrTimeOperator.InLast:    text.push('is in the last '); break;
            case RelativeDateOrTimeOperator.InNext:    text.push('is in the next '); break;
            case RelativeDateOrTimeOperator.OlderThan: text.push('is older than '); break;
          }

          text.push(this.formatUnit(filter.timeUnitType, filter.timeUnitsCount));

          if (isRelativeDateFilter(filter) && filter.operator != RelativeDateOrTimeOperator.OlderThan) {
            if (filter.includeToday) {
              text.push(', including today');
            }
          }
        }
      }
      else if (isBasicFilter(filter)) {
        target = this.fieldResolver.find(filter.target);

        if (filter.operator === BasicFilterOperator.All) {
          if (noTarget) {
            text.push('Include all');
          }
          else {
            text.push('Include all ', getTargetText());
          }
        }
        else if (filter.values.length > 0) {
          prefixWithTargetText = !noTarget;

          let valueList =
            filter.values
              .map(v => this.dataFormatter.formatForDisplay(v, {
                dataType: target.dataType,
                isAggregated: target.isAggregated,
                aggregation: target.aggregation,
                display: target.dataDisplay
              }))
              .join(', ');

          if (filter.operator === BasicFilterOperator.In) {
            if (filter.values.length == 1)
              text.push('is ');
            else text.push('is one of: ');
          }
          else {
            if (filter.values.length == 1)
              text.push('is not ');
            else text.push('is not one of: ');
          }

          text.push(valueList);
        }
      }
      else if (isTopNFilter(filter)) {
        target = this.fieldResolver.find(filter.orderBy);
     
        if (noTarget) {
          text.push((filter.operator == TopNFilterOperator.Bottom) ? 'Bottom' : 'Top');
          text.push(` ${filter.itemCount}`);
        }
        else {
          text.push((filter.operator == TopNFilterOperator.Bottom) ? 'Bottom' : 'Top');
          text.push(` ${filter.itemCount}`);
          text.push(' by ');
          text.push(getTargetText());
        }
      }
      else if (isDynamicFilter(filter)) {
        target = this.fieldResolver.find(filter.target);
        prefixWithTargetText = !noTarget;

        text.push('show data for ');

        switch (filter.value) {
          case DynamicFilterValue.SpendEmployeeCode:
            text.push('Logged in User'); break;

          case DynamicFilterValue.SpendDepartmentNames:
            text.push('Logged in Manager\'s Department(s)'); break;

          default:
            text.push('<InvalidDynamicValue>'); break;
        }
      }
      else {
        return `<${filter.filterType}>`;
      }

      text = text.filter(s => !isNullOrUndefined(s) && s.length > 0);

      if (text.length == 0) {
        if (!prefixWithTargetText)
          return '';

        text = [`has an incomplete filter`];
      }
      
      if (prefixWithTargetText)
        text = [getTargetText(), ' ', ...text];

      if (options?.capitalizeFirstWord)
        text[0] = capitalizeFirstWord(text[0]);

      return text.join('');
    }
    
    formatCondition(target: VisualFieldTarget, condition: AdvancedFilterConditionDto) {
      let operatorText;

      switch (condition.operator) {
        case AdvancedFilterConditionOperator.LessThan:           operatorText = 'is less than '; break;
        case AdvancedFilterConditionOperator.LessThanOrEqual:    operatorText = 'is less than or equal to '; break;
        case AdvancedFilterConditionOperator.GreaterThan:        operatorText = 'is greater than '; break;
        case AdvancedFilterConditionOperator.GreaterThanOrEqual: operatorText = 'is greater than or equal to '; break;

        case AdvancedFilterConditionOperator.Contains:         operatorText = 'contains '; break;
        case AdvancedFilterConditionOperator.DoesNotContain:   operatorText = 'does not contain '; break;
        case AdvancedFilterConditionOperator.StartsWith:       operatorText = 'starts with '; break;
        case AdvancedFilterConditionOperator.DoesNotStartWith: operatorText = 'does not start with '; break;

        case AdvancedFilterConditionOperator.Is:    operatorText = 'is '; break;
        case AdvancedFilterConditionOperator.IsNot: operatorText = 'is not '; break;

        case AdvancedFilterConditionOperator.IsBlank:    return `is blank`;
        case AdvancedFilterConditionOperator.IsNotBlank: return `is not blank`;
        default: return 'undefined';
      }

      let valueDisplay = this.dataFormatter.formatForDisplay(condition.value, {
        dataType: target.dataType,
        isAggregated: target.isAggregated,
        aggregation: target.aggregation,
        display: target.dataDisplay
      });

      return `${operatorText}${valueDisplay}`;
    }

    formatUnit(unit: RelativeDateTimeFilterUnit, count: number) {
      let unitConfig = DateTimeUnitConfigsMap[unit];
      let title = count > 1 ? unitConfig.title : unitConfig.titleSingular;

      return `${count} ${title.toLocaleLowerCase()}`;
    }
  }

  export function replaceSchema(filter: FilterDto, transform: (s: SchemaTargetDto) => SchemaTargetDto): Filter {
    if (isTopNFilter(filter)) {
      return {
        ...filter,
        orderBy: {
          ...filter.orderBy,
          schema: transform(filter.orderBy?.schema)
        }
      }
    }

    if (isExprFilter(filter)) {
      return {
        ...filter,
        schema: transform(filter.schema)
      }
    }

    let anyFilter = filter as any;

    return {
      ...anyFilter,
      target: {
        ...anyFilter.target,
        schema: transform(anyFilter.target?.schema)
      }
    }
  }
}
